import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  EnvelopeOpenIcon as EnvelopeOpen,
  CircleNotchIcon as Spinner,
  WarningCircleIcon as WarningCircle,
} from "@phosphor-icons/react";
import { acceptWorkspaceInvite, previewWorkspaceInvite } from "@/lib/api";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setPreviewError("Invitation token is missing from the URL.");
      return;
    }
    try {
      const data = await previewWorkspaceInvite(token);
      setPreview(data);
    } catch (e) {
      setPreviewError(formatApiErrorDetail(e?.response?.data?.detail) || "This invitation is invalid or expired.");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!preview) return;
    if (!preview.is_existing_user) {
      if (!name.trim()) {
        setError("Please enter your name.");
        return;
      }
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = { token };
      if (!preview.is_existing_user) {
        payload.name = name.trim();
        payload.password = password;
      }
      const result = await acceptWorkspaceInvite(payload);
      if (!preview.is_existing_user) {
        // New users: the backend creates the account but does NOT auto-login
        // (no cookies set). Send them to /login with the email pre-known.
        navigate("/login", {
          replace: true,
          state: { from: `/workspaces/${result.workspace_id}` },
        });
        return;
      }
      // Existing users: try to sync session in case they're already logged in.
      await refresh().catch(() => {});
      navigate(`/workspaces/${result.workspace_id}`, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to accept invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  if (previewError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
        data-testid="accept-invite-error"
      >
        <div className="ml-card w-full max-w-md p-8 text-center space-y-4">
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Invitation
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">This invitation can&apos;t be accepted</h1>
          <p className="text-sm text-[var(--ml-text-secondary)]">{previewError}</p>
          <Link to="/login" className="ml-btn-primary inline-block" data-testid="accept-invite-back-to-login">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-sm text-[var(--ml-text-secondary)]"
        data-testid="accept-invite-loading"
      >
        <Spinner size={14} className="animate-spin inline mr-2" /> Loading invitation…
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
      data-testid="accept-invite-page"
    >
      <form
        onSubmit={onSubmit}
        className="ml-card w-full max-w-md p-8 space-y-5"
        data-testid="accept-invite-form"
      >
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <EnvelopeOpen size={11} weight="bold" />
            Workspace invitation
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Join {preview.workspace_name}
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1">
            You&apos;ve been invited as <strong>{preview.role}</strong>
            {preview.client_name ? <> on the engagement for <strong>{preview.client_name}</strong></> : null}.
          </p>
        </div>

        <div className="text-sm bg-[var(--ml-surface)] border border-[var(--ml-border)] px-3 py-2" data-testid="accept-invite-email">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
            Invitation email
          </span>
          {preview.email}
        </div>

        {!preview.is_existing_user && (
          <>
            <p className="text-sm text-[var(--ml-text-secondary)]">
              Create an account to accept. Use the email above as your login.
            </p>
            <label className="block">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
                Full name
              </div>
              <input
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="accept-invite-name"
                className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
              />
            </label>
            <label className="block">
              <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
                Choose a password
              </div>
              <input
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="accept-invite-password"
                className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
              />
              <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] mt-1">Minimum 8 characters.</div>
            </label>
          </>
        )}

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-[#a2191f] bg-[#fff1f1] border border-[#ffd7d9] px-3 py-2"
            data-testid="accept-invite-error-msg"
            role="alert"
          >
            <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="accept-invite-submit"
          className="w-full px-4 py-2 bg-[var(--ml-primary)] text-white font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner size={16} className="animate-spin" /> : null}
          {submitting
            ? "Accepting…"
            : preview.is_existing_user
              ? "Accept invitation"
              : "Create account & accept"}
        </button>

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          <Link to="/login" className="text-[var(--ml-primary)] hover:underline font-semibold" data-testid="accept-invite-to-login">
            Sign in instead
          </Link>
        </div>
      </form>
    </div>
  );
}
